import { TableBody, TableRow } from "@mui/material";
import SortableTableHead, { HeadCell } from "components/Common/Table/SortableTableHead";
import { StyledTableWrapper } from "components/Common/Table/StyledTable.styles";
import {
	BillToText,
	CabinetCountText,
	CreatedDateText,
	CubeCountText,
	JobNameText,
	PONumberText,
	ProductLineText
} from "constants/text";
import { useHandleTableSort } from "hooks/useHandleTableSort";
import { useCallback } from "react";
import { models } from "types/api/viewModels";
import { TruckOrdersTableWrapper } from "./TruckOrdersTable.styles";
import { useSelector } from "react-redux";
import { RootState } from "stores/application.store.tsx";
import TruckOrdersTableRow from "components/Trucks/TruckOrdersTable/components/TruckOrdersTableRow/TruckOrdersTableRow.tsx";

interface TruckOrdersTable {
	drafts: models["DraftOrderViewModel"][];
	billTos: models["BillToViewModel"][];
}

export const truckOrdersTableHeadCells = [
	{
		id: "createdDate",
		numeric: false,
		disablePadding: false,
		label: CreatedDateText,
		sortable: true
	},
	{
		id: "billTo",
		numeric: false,
		disablePadding: false,
		label: BillToText,
		sortable: false
	},
	{
		id: "poNumber",
		numeric: false,
		disablePadding: false,
		label: PONumberText,
		sortable: false
	},
	{
		id: "jobName",
		numeric: false,
		disablePadding: false,
		label: JobNameText,
		sortable: false
	},
	{
		id: "productLine",
		numeric: false,
		disablePadding: false,
		label: ProductLineText,
		sortable: false
	},
	{
		id: "cabinetCount",
		numeric: false,
		disablePadding: false,
		label: CabinetCountText,
		sortable: false
	},
	{
		id: "cubeCount",
		numeric: false,
		disablePadding: false,
		label: CubeCountText,
		sortable: false
	},
	{
		id: "empty",
		numeric: false,
		disablePadding: false,
		label: "",
		sortable: false
	}
] as const satisfies readonly HeadCell[];

const TruckOrdersTable = ({ drafts, billTos }: TruckOrdersTable) => {
	const { getComparator, handleRequestSort, order, orderBy } = useHandleTableSort("createdDate", "asc");
	const truck = useSelector((state: RootState) => state.truck.truck);

	const sortDrafts = useCallback(
		(drafts: models["DraftOrderViewModel"][] | undefined) => drafts?.slice().sort(getComparator(order, orderBy)),
		[getComparator, order, orderBy]
	);

	return (
		<TruckOrdersTableWrapper>
			<StyledTableWrapper
				data-testid="truck-orders-table"
				aria-label="Truck Orders Table"
			>
				<SortableTableHead
					orderBy={orderBy}
					order={order}
					onRequestSort={handleRequestSort}
					headCells={truckOrdersTableHeadCells}
				/>
				<TableBody>
					{sortDrafts(drafts)?.map((draft) => (
						<TableRow
							key={draft.draftOrderId}
							data-testid="truck-orders-table-row"
						>
							<TruckOrdersTableRow
								draft={draft}
								billTos={billTos}
								truck={truck}
							/>
						</TableRow>
					))}
				</TableBody>
			</StyledTableWrapper>
		</TruckOrdersTableWrapper>
	);
};

export default TruckOrdersTable;
